import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Filter, Gift, Star, RefreshCw, AlertCircle, Grid3X3, List } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import type { Benefit } from "../types/benefit";
import { fetchBenefits } from "../services/fetchBenefits";
import { pushGA } from "../lib/utils";
import useDarkMode from "../hooks/useDarkMode";
import Badge from "../components/Badge";
import BenefitCard from "../components/BenefitCard";
import CardsSkeleton from "../components/CardsSkeleton";
import EmptyState from "../components/EmptyState";
import UserMenu from "../components/UserMenu";
import sampleBenefits from "../data/sampleBenefits";

const BENEFITS_URL = "https://docs.google.com/spreadsheets/d/15xtru2OiEObJC6OZJaNUUXSOnLn_8WRsgBwCwTfDvyM/gviz/tq?tqx=out:json&gid=0";

const CATEGORIES_ORDER = ["Todos", "Desconto", "Plano", "Parceria"];

export default function BenefitsPage() {
  const [benefits, setBenefits] = useState<Benefit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [useSample, setUseSample] = useState(false);
  const [category, setCategory] = useState<string>("Todos");
  const [q, setQ] = useState("");
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  const doFetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchBenefits(BENEFITS_URL);
      setBenefits(data);
      setUseSample(false);
    } catch (e: any) {
      setError(e?.message || 'Falha ao buscar benefícios');
      setBenefits(sampleBenefits);
      setUseSample(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { 
    doFetch(); 
    pushGA('page_view', { page_title: 'Benefícios Embaixadores' });
  }, []);

  const categories = useMemo(() => {
    const set = new Set<string>(["Todos"]);
    benefits.forEach((b) => set.add(b.categoria));
    return Array.from(set).sort((a, b) => {
      const ia = CATEGORIES_ORDER.indexOf(a);
      const ib = CATEGORIES_ORDER.indexOf(b);
      if (ia === -1 && ib === -1) return a.localeCompare(b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  }, [benefits]);

  const filtered = useMemo(() => {
    return benefits.filter((b) => {
      const byCat = category === "Todos" || b.categoria === category;
      const byQ = q
        ? (b.nome + " " + b.descricao + " " + b.categoria)
            .toLowerCase()
            .includes(q.toLowerCase())
        : true;
      return byCat && byQ;
    });
  }, [benefits, category, q]);

  const highlights = useMemo(() => 
    benefits.filter((b) => (b.destaque || "").toLowerCase() === "sim"), 
    [benefits]
  );

  return (
    <>
      {/* Hero Section */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="py-8 md:py-12">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 rounded-full bg-darling-lilac/10 dark:bg-darling-lilac/20 px-4 py-2 mb-6">
              <Gift className="h-4 w-4 text-darling-lilac" />
              <span className="text-sm font-exo font-medium text-darling-lilac">
                Exclusivo para Embaixadores
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-exo font-black text-soft-black dark:text-white mb-6 leading-tight">
              <span className="bg-gradient-to-r from-darling-lilac via-deep-purple to-ocean-water bg-clip-text text-transparent">
                Benefícios
              </span>
              <br />
              <span className="text-soft-black dark:text-white">
                Embaixadores
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-soft-black/70 dark:text-light-gray/80 font-exo font-light leading-relaxed max-w-3xl mx-auto">
              Descontos exclusivos, planos especiais e parcerias desenvolvidas especialmente para nossa comunidade de embaixadores.
            </p>
          </div>
        </div>

        {/* Filtros */}
        <div className="pb-8">
          <div className="rounded-2xl border border-light-gray-alt dark:border-deep-purple/30 bg-white/80 dark:bg-soft-black/80 backdrop-blur-sm px-6 py-6 shadow-lg">
            <div className="flex flex-col gap-6">
              {/* Search Bar */}
              <div className="flex items-center gap-3 rounded-xl border border-light-gray-alt dark:border-deep-purple/50 bg-white dark:bg-soft-black px-4 py-3 shadow-sm focus-within:ring-2 focus-within:ring-darling-lilac/20 focus-within:border-darling-lilac/50 transition-all">
                <Search className="h-5 w-5 text-darling-lilac flex-shrink-0" />
                <input
                  className="w-full bg-transparent outline-none text-sm font-exo placeholder:text-soft-black/50 dark:placeholder:text-light-gray/50 text-soft-black dark:text-white"
                  placeholder="Buscar por benefício, categoria ou descrição..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onBlur={() => pushGA('filter_apply', { q, category })}
                />
              </div>
              
              {/* Filter Categories */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-soft-black/70 dark:text-light-gray/90 font-exo font-medium">
                  <Filter className="h-4 w-4"/>
                  <span>Filtros por categoria</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      className={`px-4 py-2 rounded-lg text-sm font-exo font-medium border transition-all duration-200 ${
                        category === cat 
                          ? "bg-darling-lilac text-white border-darling-lilac shadow-md hover:shadow-lg" 
                          : "bg-light-gray dark:bg-deep-purple/30 hover:bg-light-gray-alt dark:hover:bg-deep-purple/50 text-soft-black dark:text-white border-light-gray-alt dark:border-deep-purple/50"
                      }`}
                      onClick={() => { 
                        setCategory(cat); 
                        pushGA('filter_apply', { q, category: cat }); 
                      }}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>

              {/* Status Bar */}
              <div className="flex flex-wrap items-center justify-between gap-3 pt-3 border-t border-light-gray-alt dark:border-deep-purple/30">
                <div className="flex flex-wrap items-center gap-3 text-xs">
                  {/* View Mode Toggle */}
                  <div className="flex items-center gap-1 rounded-lg border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray/50 dark:bg-deep-purple/20 p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-1.5 rounded text-xs transition-all ${
                        viewMode === 'grid'
                          ? 'bg-darling-lilac text-white'
                          : 'text-soft-black/60 dark:text-light-gray/60 hover:text-soft-black dark:hover:text-white'
                      }`}
                      title="Visualização em grade"
                    >
                      <Grid3X3 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-1.5 rounded text-xs transition-all ${
                        viewMode === 'list'
                          ? 'bg-darling-lilac text-white'
                          : 'text-soft-black/60 dark:text-light-gray/60 hover:text-soft-black dark:hover:text-white'
                      }`}
                      title="Visualização em lista"
                    >
                      <List className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  
                  {useSample && (
                    <Badge className="bg-sun-flower/20 text-sun-flower border-sun-flower/30 dark:bg-sun-flower/10 dark:text-sun-flower dark:border-sun-flower/20">
                      <AlertCircle className="h-3 w-3" />
                      Dados de exemplo
                    </Badge>
                  )}
                  {!useSample && (
                    <Badge className="bg-ocean-water/20 text-ocean-water border-ocean-water/30 dark:bg-ocean-water/10 dark:text-ocean-water dark:border-ocean-water/20">
                      <div className="w-2 h-2 rounded-full bg-ocean-water animate-pulse" />
                      Dados atualizados
                    </Badge>
                  )}
                  {error && (
                    <span className="inline-flex items-center gap-1 text-sun-flower dark:text-sun-flower font-exo">
                      <AlertCircle className="h-3.5 w-3.5"/> 
                      {error}
                    </span>
                  )}
                </div>
                <button
                  onClick={doFetch} 
                  className="inline-flex items-center gap-2 rounded-lg border border-light-gray-alt dark:border-deep-purple/50 bg-light-gray/50 dark:bg-deep-purple/20 hover:bg-light-gray dark:hover:bg-deep-purple/40 px-3 py-1.5 text-xs font-exo font-medium text-soft-black dark:text-white transition-all duration-200"
                >
                  <RefreshCw className="h-3.5 w-3.5"/> 
                  Atualizar
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Highlights Section */}
        {highlights.length > 0 && !loading && (
          <section className="mb-12">
            <div className="flex items-center gap-3 text-soft-black dark:text-white mb-6">
              <Star className="h-5 w-5 text-sun-flower fill-current" />
              <h2 className="text-xl md:text-2xl font-exo font-bold tracking-wide">Em destaque</h2>
            </div>
            <div className={`${viewMode === 'grid' ? 'grid sm:grid-cols-2 xl:grid-cols-3 gap-8 items-stretch' : 'space-y-4'}`}>
              <AnimatePresence>
                {highlights.map((benefit, idx) => (
                  <BenefitCard key={benefit.nome + idx} benefit={benefit} viewMode={viewMode} />
                ))}
              </AnimatePresence>
            </div>
          </section>
        )}

        {/* All Benefits Section */}
        <section>
          <div className="flex items-center gap-3 text-soft-black dark:text-white mb-6">
            <Gift className="h-5 w-5" />
            <h2 className="text-xl md:text-2xl font-exo font-bold tracking-wide">Todos os benefícios</h2>
          </div>
          
          {loading ? (
            <CardsSkeleton />
          ) : filtered.length === 0 ? (
            <EmptyState title="Nenhum benefício encontrado">
              Tente ajustar os filtros ou buscar por outros termos.
            </EmptyState>
          ) : (
            <div className={`${viewMode === 'grid' ? 'grid sm:grid-cols-2 xl:grid-cols-3 gap-8 items-stretch' : 'space-y-4'}`}>
              <AnimatePresence>
                {filtered.map((benefit, idx) => (
                  <BenefitCard key={benefit.nome + idx} benefit={benefit} viewMode={viewMode} />
                ))}
              </AnimatePresence>
            </div>
          )}
        </section>

        {/* FAQ Section */}
        <section className="mt-16 pt-8 border-t border-light-gray-alt dark:border-deep-purple/50">
          <h3 className="text-lg font-exo font-bold text-soft-black dark:text-white mb-6">
            Perguntas frequentes
          </h3>
          <div className="grid md:grid-cols-2 gap-6 text-sm">
            <div className="space-y-4">
              <div>
                <h4 className="font-exo font-semibold text-soft-black dark:text-white mb-2">
                  Como resgatar os benefícios?
                </h4>
                <p className="text-soft-black/70 dark:text-light-gray/80 font-exo font-light">
                  Clique em "Acessar benefício" no card e siga as instruções da página de destino do parceiro.
                </p>
              </div>
              <div>
                <h4 className="font-exo font-semibold text-soft-black dark:text-white mb-2">
                  Preciso ter um plano para acessar?
                </h4>
                <p className="text-soft-black/70 dark:text-light-gray/80 font-exo font-light">
                  Alguns benefícios exigem vínculo ativo como embaixador; isso será indicado no detalhe do benefício.
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="font-exo font-semibold text-soft-black dark:text-white mb-2">
                  Os descontos são acumulativos?
                </h4>
                <p className="text-soft-black/70 dark:text-light-gray/80 font-exo font-light">
                  Normalmente não. Verifique as condições específicas no link do parceiro.
                </p>
              </div>
              <div>
                <h4 className="font-exo font-semibold text-soft-black dark:text-white mb-2">
                  Como sugerir novos benefícios?
                </h4>
                <p className="text-soft-black/70 dark:text-light-gray/80 font-exo font-light">
                  Entre em contato com a equipe CRO Brasil através dos canais oficiais da comunidade.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}